import type { Experiment } from 'types';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { experimentTypeLabel } from 'definitions';
import { useClient } from 'hooks/useClient';
import { toast } from 'sonner';

const FIELD_INPUT = 'border-line bg-field text-foreground h-10 rounded-lg text-body';
const SECONDARY_BUTTON = 'border-line text-foreground hover:bg-hover h-9 rounded-lg bg-transparent';

type DuplicateExperimentModalProps = {
  open: boolean;
  onClose: () => void;
  onDuplicate: (experimentId: number, name: string) => Promise<void>;
};

export function DuplicateExperimentModal({ open, onClose, onDuplicate }: DuplicateExperimentModalProps) {
  const [experimentId, setExperimentId] = useState('');
  const [experimentName, setExperimentName] = useState('');
  const [experimentType, setExperimentType] = useState('');
  const [fetchedWebsiteUrl, setFetchedWebsiteUrl] = useState('');
  const [duplicateName, setDuplicateName] = useState('');
  const [fetchError, setFetchError] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const apiClient = useClient();

  const resetState = () => {
    setExperimentId('');
    setExperimentName('');
    setExperimentType('');
    setFetchedWebsiteUrl('');
    setDuplicateName('');
    setFetchError('');
    setHasFetched(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFetchExperiment = async () => {
    if (!experimentId.trim()) return;

    setIsFetching(true);
    setFetchError('');

    try {
      const experiment = await apiClient.get<Experiment>(`/experiments/${experimentId.trim()}`);
      if (!experiment) {
        setFetchError('Experiment not found');
        setHasFetched(false);
        return;
      }
      setExperimentName(experiment.name);
      setExperimentType(experimentTypeLabel(experiment.type));
      setFetchedWebsiteUrl(experiment.websiteUrl ?? experiment.website?.url ?? '');
      setDuplicateName(`${experiment.name} (test from ${experimentId.trim()})`);
      setHasFetched(true);
    } catch (error) {
      setFetchError('Failed to fetch experiment. Please check the ID and try again.');
      setHasFetched(false);
    } finally {
      setIsFetching(false);
    }
  };

  const handleDuplicate = async () => {
    if (!experimentId.trim() || !duplicateName.trim()) return;

    try {
      await onDuplicate(+experimentId.trim(), duplicateName.trim());
      toast.success('Experiment duplicated successfully');
      handleClose();
    } catch (error) {
      toast.error('Failed to duplicate experiment');
      console.error(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Duplicate Experiment</DialogTitle>
          <DialogDescription>Enter the experiment ID you want to duplicate.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <label htmlFor="experiment-id" className="micro-label block">
              Experiment ID
            </label>
            <div className="flex gap-2">
              <Input
                id="experiment-id"
                type="number"
                value={experimentId}
                onChange={(ev) => {
                  setExperimentId(ev.target.value);
                  setFetchError('');
                  setHasFetched(false);
                }}
                placeholder="Enter experiment ID"
                disabled={isFetching}
                className={FIELD_INPUT}
              />
              <Button
                onClick={handleFetchExperiment}
                disabled={!experimentId.trim() || isFetching}
                className="h-10 shrink-0 rounded-lg"
              >
                {isFetching ? 'Fetching...' : 'Fetch'}
              </Button>
            </div>
            {fetchError && <p className="text-danger m-0 text-micro">{fetchError}</p>}
          </div>

          {hasFetched && (
            <div className="space-y-1.5">
              <div className="bg-panel2 border-line rounded-card border p-3">
                <div className="micro-label">Found experiment</div>
                <div className="text-foreground mt-1 text-meta font-medium">{experimentName}</div>
                <div className="supporting mt-1">
                  {experimentType && <span className="mr-2">{experimentType}</span>}
                  {fetchedWebsiteUrl && <span>{fetchedWebsiteUrl}</span>}
                </div>
              </div>

              <label htmlFor="duplicate-name" className="micro-label block pt-1.5">
                New Experiment Name
              </label>
              <Input
                id="duplicate-name"
                value={duplicateName}
                onChange={(ev) => setDuplicateName(ev.target.value)}
                placeholder="Enter name for the duplicated experiment"
                className={FIELD_INPUT}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className={SECONDARY_BUTTON}>
            Cancel
          </Button>
          {hasFetched && (
            <Button onClick={handleDuplicate} disabled={!duplicateName.trim()} className="h-9 rounded-lg">
              Duplicate
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
